import type { ApiResponse } from '@/types';
import { apiCache, syncQueue, offlineDocuments } from './offline';

// Use environment variable for production, fallback to relative path for dev proxy
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Cache TTL configurations (in seconds)
const CACHE_TTL = {
  default: 3600,        // 1 hour
  user: 1800,           // 30 minutes
  documents: 7200,      // 2 hours
  static: 86400,        // 24 hours
};

class ApiClient {
  private authToken: string | null = null;
  private refreshToken: string | null = null;
  private mdaCode: string | null = null;
  private isPlatformAdmin: boolean = false;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;

  setAuthToken(token: string | null) {
    this.authToken = token;
    // Also store in localStorage for service worker access
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  setRefreshToken(token: string | null) {
    this.refreshToken = token;
  }

  setIsPlatformAdmin(isPlatform: boolean) {
    this.isPlatformAdmin = isPlatform;
  }

  setMdaCode(code: string | null) {
    this.mdaCode = code;
  }

  getMdaCode(): string | null {
    return this.mdaCode;
  }

  /**
   * Attempt to refresh the access token
   */
  private async tryRefreshToken(): Promise<boolean> {
    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const endpoint = this.isPlatformAdmin ? '/api/platform/auth/refresh' : '/api/v1/auth/refresh';
      const body = this.isPlatformAdmin
        ? { refreshToken: this.refreshToken }
        : { refreshToken: this.refreshToken, mdaCode: this.mdaCode };

      let url: string;
      if (API_BASE_URL.startsWith('http')) {
        url = `${API_BASE_URL}${endpoint}`;
      } else {
        url = endpoint;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data.success && data.data) {
        this.setAuthToken(data.data.accessToken);
        this.refreshToken = data.data.refreshToken;

        // Update localStorage
        const stored = localStorage.getItem('ohcs_auth');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            parsed.accessToken = data.data.accessToken;
            parsed.refreshToken = data.data.refreshToken;
            localStorage.setItem('ohcs_auth', JSON.stringify(parsed));
          } catch {
            // Ignore parse errors
          }
        }

        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    if (this.mdaCode) {
      headers['X-MDA-Code'] = this.mdaCode;
    }

    return headers;
  }

  /**
   * Determine cache TTL based on endpoint
   */
  private getCacheTTL(endpoint: string): number {
    if (endpoint.includes('/users')) return CACHE_TTL.user;
    if (endpoint.includes('/documents')) return CACHE_TTL.documents;
    if (endpoint.includes('/departments') || endpoint.includes('/roles')) return CACHE_TTL.static;
    return CACHE_TTL.default;
  }

  /**
   * Check if endpoint should use cache
   */
  private shouldCache(method: string, endpoint: string): boolean {
    // Only cache GET requests
    if (method !== 'GET') return false;

    // Don't cache auth endpoints
    if (endpoint.includes('/auth/')) return false;

    // Don't cache search/filter endpoints with dynamic params
    if (endpoint.includes('?search=')) return false;

    return true;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    body?: unknown,
    options?: RequestInit & { skipCache?: boolean; queueIfOffline?: boolean }
  ): Promise<ApiResponse<T>> {
    // Build the full URL
    let url: string;
    if (API_BASE_URL.startsWith('http')) {
      // Production: use full API URL
      // Remove /api prefix from endpoint if present since API_BASE_URL is the worker root
      const cleanEndpoint = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
      url = `${API_BASE_URL}${cleanEndpoint}`;
    } else {
      // Development: use relative path with Vite proxy
      url = endpoint.startsWith('/api') ? endpoint : `${API_BASE_URL}${endpoint}`;
    }
    const fullUrl = url.startsWith('http') ? url : new URL(url, window.location.origin).href;
    const useCache = this.shouldCache(method, endpoint) && !options?.skipCache;

    // For GET requests, try cache first if offline
    if (method === 'GET' && useCache && !navigator.onLine) {
      const cached = await apiCache.get<ApiResponse<T>>(fullUrl);
      if (cached) {
        console.log(`[API] Serving from cache: ${endpoint}`);
        return cached;
      }
    }

    try {
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        ...options,
      });

      // Handle non-JSON responses (like 405 errors that may return empty body)
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          data = null;
        }
      } else {
        // Try to parse as JSON, but fallback to null if it fails
        const text = await response.text();
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = null;
        }
      }

      if (!response.ok) {
        // If 401, try to refresh token and retry
        if (response.status === 401 && !endpoint.includes('/auth/')) {
          const refreshed = await this.tryRefreshToken();
          if (refreshed) {
            // Retry the request with new token
            const retryResponse = await fetch(url, {
              method,
              headers: this.getHeaders(),
              body: body ? JSON.stringify(body) : undefined,
            });

            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              return retryData as ApiResponse<T>;
            }
          }
        }

        return {
          success: false,
          error: data?.error || {
            code: 'REQUEST_FAILED',
            message: `Request failed with status ${response.status}${response.statusText ? `: ${response.statusText}` : ''}`,
          },
        };
      }

      const result = data as ApiResponse<T>;

      // Cache successful GET responses
      if (method === 'GET' && useCache && result.success) {
        const ttl = this.getCacheTTL(endpoint);
        await apiCache.set(fullUrl, result, ttl);
      }

      return result;
    } catch (error) {
      // If offline and it's a mutation, queue for later
      if (!navigator.onLine && options?.queueIfOffline && method !== 'GET') {
        const queueId = await syncQueue.add({
          type: method === 'POST' ? 'create' : method === 'DELETE' ? 'delete' : 'update',
          endpoint: url,
          method: method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
          payload: body,
          headers: this.getHeaders() as Record<string, string>,
          priority: 1,
        });

        console.log(`[API] Request queued for sync: ${endpoint} (ID: ${queueId})`);

        // Return optimistic response
        return {
          success: true,
          data: { queued: true, queueId } as unknown as T,
        };
      }

      // For GET requests when offline, try cache as fallback
      if (method === 'GET' && useCache) {
        const cached = await apiCache.get<ApiResponse<T>>(fullUrl);
        if (cached) {
          console.log(`[API] Serving stale cache: ${endpoint}`);
          return cached;
        }
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error occurred',
        },
      };
    }
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
    options?: { skipCache?: boolean }
  ): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return this.request<T>('GET', url, undefined, options);
  }

  async post<T>(endpoint: string, body?: unknown, options?: { queueIfOffline?: boolean }): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, body, options);
  }

  async put<T>(endpoint: string, body?: unknown, options?: { queueIfOffline?: boolean }): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, body, options);
  }

  async patch<T>(endpoint: string, body?: unknown, options?: { queueIfOffline?: boolean }): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, body, options);
  }

  async delete<T>(endpoint: string, options?: { queueIfOffline?: boolean }): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  async upload<T>(endpoint: string, file: File, additionalData?: Record<string, string>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE_URL}${endpoint}`;

    // Can't queue file uploads when offline
    if (!navigator.onLine) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: 'File upload requires an internet connection',
        },
      };
    }

    try {
      const headers: HeadersInit = {};
      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`;
      }
      if (this.mdaCode) {
        headers['X-MDA-Code'] = this.mdaCode;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || {
            code: 'UPLOAD_FAILED',
            message: `Upload failed with status ${response.status}`,
          },
        };
      }

      return data as ApiResponse<T>;
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error occurred',
        },
      };
    }
  }

  async download(endpoint: string, options?: { saveOffline?: boolean; documentId?: string; pfileId?: string }): Promise<Blob | null> {
    const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE_URL}${endpoint}`;

    // Check offline cache first
    if (!navigator.onLine && options?.documentId) {
      const cachedDoc = await offlineDocuments.get(options.documentId);
      if (cachedDoc) {
        console.log(`[API] Serving document from offline cache: ${options.documentId}`);
        return cachedDoc.blob;
      }
    }

    try {
      const headers: HeadersInit = {};
      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`;
      }
      if (this.mdaCode) {
        headers['X-MDA-Code'] = this.mdaCode;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        // Try offline cache as fallback
        if (options?.documentId) {
          const cachedDoc = await offlineDocuments.get(options.documentId);
          if (cachedDoc) {
            return cachedDoc.blob;
          }
        }
        return null;
      }

      const blob = await response.blob();

      // Save to offline cache if requested
      if (options?.saveOffline && options.documentId) {
        const contentDisposition = response.headers.get('content-disposition');
        const fileName = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || 'document'
          : 'document';

        await offlineDocuments.save({
          documentId: options.documentId,
          name: fileName,
          mimeType: blob.type,
          blob,
          size: blob.size,
          pfileId: options.pfileId,
        });

        console.log(`[API] Document saved for offline access: ${options.documentId}`);
      }

      return blob;
    } catch {
      // Try offline cache as fallback
      if (options?.documentId) {
        const cachedDoc = await offlineDocuments.get(options.documentId);
        if (cachedDoc) {
          console.log(`[API] Serving document from offline cache (fallback): ${options.documentId}`);
          return cachedDoc.blob;
        }
      }
      return null;
    }
  }

  /**
   * Clear all cached data (for logout)
   */
  async clearCache(): Promise<void> {
    await apiCache.clear();
    console.log('[API] Cache cleared');
  }

  /**
   * Invalidate specific cache entries
   */
  async invalidateCache(pattern: string): Promise<void> {
    // For now, just clear all cache
    // In a more sophisticated implementation, we'd iterate and match patterns
    await apiCache.clear();
    console.log(`[API] Cache invalidated for pattern: ${pattern}`);
  }
}

export const api = new ApiClient();
